import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router';
import { api } from '../api';
import { TopBar } from '../components/TopBar';
import { useMe } from './queries';

interface DirectorAccount {
  sub: string;
  email: string;
  status: string;
  enabled: boolean;
  admin: boolean;
  createdAtMs: number | null;
  races: number;
  name: string | null;
  organization: string | null;
  expectedRunners: number | null;
  referral: string | null;
  plan: 'free' | 'unlimited';
  lastSeenMs: number | null;
}

const statusText: Record<string, { label: string; chip: string }> = {
  CONFIRMED: { label: 'Active', chip: 'ok' },
  FORCE_CHANGE_PASSWORD: { label: 'Invited, not signed in yet', chip: 'warn' },
  RESET_REQUIRED: { label: 'Password reset needed', chip: 'warn' },
};

/** Admins: who can sign in, invitations, and who else is an admin. */
export function DirectorsPage() {
  const qc = useQueryClient();
  const me = useMe();
  const list = useQuery({ queryKey: ['directors'], queryFn: () => api<{ directors: DirectorAccount[] }>('/admin/directors'), enabled: !!me.data?.admin });
  const [email, setEmail] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: (to: string) => api<{ resent: boolean }>('/admin/directors', { body: { email: to } }),
    onSuccess: ({ resent }, to) => {
      setNote(`${resent ? 'Re-sent the invitation to' : 'Invited'} ${to}. They’ll get an email with a temporary password and a sign-in link.`);
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['directors'] });
    },
  });
  const setPlan = useMutation({
    mutationFn: ({ sub, plan }: { sub: string; plan: DirectorAccount['plan'] }) => api(`/admin/directors/${encodeURIComponent(sub)}/plan`, { method: 'PUT', body: { plan } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['directors'] }),
  });
  const setAdmin = useMutation({
    mutationFn: ({ sub, admin }: { sub: string; admin: boolean }) => api(`/admin/directors/${encodeURIComponent(sub)}/admin`, { method: 'PUT', body: { admin } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['directors'] }),
  });

  if (me.data && !me.data.admin) return <Navigate to="/" replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setNote(null);
    invite.mutate(email.trim());
  };

  return (
    <>
      <TopBar />
      <main className="page">
        <div className="page-head">
          <div>
            <h1>Race directors</h1>
            <p>Everyone who can sign in, including self sign-ups. Directors see only their own races; admins see every race and this page.</p>
          </div>
        </div>
        <div className="row">
          <section className="card flush main-col" style={{ gap: 0 }}>
            <div className="card-head">
              <h2>Accounts {list.data ? `(${list.data.directors.length})` : ''}</h2>
            </div>
            {list.isLoading && <p className="empty">Loading…</p>}
            {list.error && <div className="notice bad" style={{ margin: 16 }}>{(list.error as Error).message}</div>}
            {setAdmin.error && <div className="notice bad" style={{ margin: 16 }}>{(setAdmin.error as Error).message}</div>}
            {list.data && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Director</th>
                      <th>Status</th>
                      <th className="num">Races</th>
                      <th>Plan</th>
                      <th>Admin</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.directors.map((d) => {
                      const st = statusText[d.status] ?? { label: d.status.toLowerCase().replace(/_/g, ' '), chip: 'neutral' };
                      const self = d.sub === me.data?.sub;
                      return (
                        <tr key={d.sub}>
                          <td>
                            <b>{d.name ?? d.email}</b>
                            {self && <span className="small muted"> (you)</span>}
                            {d.name && <div className="small">{d.email}</div>}
                            {d.organization && <div className="small">{d.organization}</div>}
                            <div className="small muted">
                              {d.createdAtMs ? `Joined ${new Date(d.createdAtMs).toLocaleDateString()}` : ''}
                              {d.lastSeenMs ? ` · last seen ${new Date(d.lastSeenMs).toLocaleDateString()}` : ''}
                              {d.expectedRunners ? ` · ~${d.expectedRunners} runners` : ''}
                              {d.referral ? ` · heard via ${d.referral}` : ''}
                            </div>
                          </td>
                          <td>
                            <span className={`chip ${d.enabled ? st.chip : 'bad'}`}>{d.enabled ? st.label : 'Disabled'}</span>
                          </td>
                          <td className="num mono">{d.races}</td>
                          <td>
                            <select className="input" style={{ width: 'auto', minHeight: 36 }} value={d.plan} disabled={setPlan.isPending} onChange={(e) => setPlan.mutate({ sub: d.sub, plan: e.target.value as DirectorAccount['plan'] })} aria-label={`Plan: ${d.email}`}>
                              <option value="free">Free (1,000 runners/race)</option>
                              <option value="unlimited">Unlimited</option>
                            </select>
                          </td>
                          <td>
                            <label className="check" style={{ alignItems: 'center' }}>
                              <input
                                type="checkbox"
                                checked={d.admin}
                                disabled={self || setAdmin.isPending}
                                onChange={(e) => {
                                  const admin = e.target.checked;
                                  if (confirm(admin ? `Make ${d.email} an admin? They’ll see every race and can invite directors.` : `Remove admin access from ${d.email}?`)) setAdmin.mutate({ sub: d.sub, admin });
                                }}
                                aria-label={`Admin: ${d.email}`}
                              />
                            </label>
                          </td>
                          <td>
                            {d.status === 'FORCE_CHANGE_PASSWORD' && (
                              <button type="button" className="btn small" disabled={invite.isPending} onClick={() => invite.mutate(d.email)}>
                                Resend invite
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="small muted" style={{ margin: 16 }}>
              Admin changes take effect the next time that person signs in.
            </p>
          </section>
          <form className="card side-col stack" onSubmit={submit} style={{ gap: 14 }}>
            <h2>Invite a director</h2>
            <label className="field">
              Email
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="director@runningclub.org" required />
            </label>
            <p className="small muted" style={{ margin: 0 }}>
              They get an email with a temporary password and choose their own the first time they sign in. Invitations expire after 7 days; resend from the list.
            </p>
            {invite.error && <div className="notice bad">{(invite.error as Error).message}</div>}
            {note && <div className="notice ok">{note}</div>}
            <button className="btn primary" type="submit" disabled={invite.isPending}>
              Send invitation
            </button>
          </form>
        </div>
      </main>
    </>
  );
}
